import { useEffect, useState } from "react";
import { honeyStockApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../common/Card";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { Modal } from "../common/Modal";
import { Field } from "../common/Field";
import { Toggle } from "../common/Toggle";

/**
 * Jars on the shelf, clean and dirty (#229).
 *
 * OPT-IN, even on Business. Plenty of sideliners will never count glass, and
 * forcing it makes bottling slower for no gain. So this panel starts EMPTY
 * and says what it would do, rather than presenting a grid of zeros.
 *
 * That distinction is the whole design: an absent row means "not tracking",
 * never "no jars". A confident 0 that actually means "we don't know" is the
 * kind of number somebody acts on -- they would go and buy glass they
 * already have.
 *
 * Its own component because the pool page is already large, and because
 * testing "does the empty state offer to start" inside it would mean mocking
 * the pool, sizes, movements and varietals to assert on one sentence.
 */
export function GlassInventoryPanel({ sizes }) {
  const { notify } = useToast();
  const [glass, setGlass] = useState(null);
  const [enforcing, setEnforcing] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);
  const [washOpen, setWashOpen] = useState(false);
  const [trackForm, setTrackForm] = useState({ bottle_size_id: "", clean_count: "", dirty_count: "" });
  const [washForm, setWashForm] = useState({ bottle_size_id: "", count: "" });

  async function load() {
    try {
      const r = await honeyStockApi.listGlass();
      setGlass(r.glass || []);
    } catch {
      // Best-effort: glass is a convenience. A failure here must not take
      // down the pool page it sits on.
      setGlass([]);
    }
    /* Read beside the counts it governs (#360). Best-effort: a failure
       leaves the toggle off, which is the default behaviour anyway, so
       nothing is misreported. */
    honeyStockApi.jarEnforcement()
      .then((r) => setEnforcing(Boolean(r.enforce_jar_count)))
      .catch(() => {});
  }
  useEffect(() => { load(); }, []);

  // What is on record for the size currently chosen in the form, when the
  // account is already counting it (#260). undefined means this size is
  // being counted for the first time.
  const trackingNow = (glass || []).find(
    (g) => String(g.bottle_size_id) === String(trackForm.bottle_size_id)
  );

  async function submitTrack() {
    const sizeId = Number(trackForm.bottle_size_id);
    if (!sizeId) {
      notify("Pick a jar size", "warning");
      return;
    }
    try {
      /*
       * A blank box KEEPS the current count; it does not mean zero (#260).
       *
       * start_tracking is a MERGE whose matched branch is `UPDATE SET
       * clean_count = ?, dirty_count = ?` -- it REPLACES. Combined with the
       * old `Number("") || 0`, somebody who opened this to correct their
       * clean count and left the dirty box alone silently zeroed their
       * dirty jars. Nothing on the screen said the counts were being
       * replaced, so there was no reason to think filling in one box would
       * touch the other.
       *
       * Keeping the current value is what leaving a box empty MEANS.
       * Setting a count to zero is still possible by typing 0 -- which is
       * the deliberate act it should be.
       */
      const keepOr = (entered, current) => (entered === "" ? (current ?? 0) : Number(entered));
      await honeyStockApi.trackGlass({
        bottle_size_id: sizeId,
        clean_count: keepOr(trackForm.clean_count, trackingNow?.clean_count),
        dirty_count: keepOr(trackForm.dirty_count, trackingNow?.dirty_count),
      });
      setTrackOpen(false);
      setTrackForm({ bottle_size_id: "", clean_count: "", dirty_count: "" });
      load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't save those counts", "danger");
    }
  }

  async function submitWash() {
    const sizeId = Number(washForm.bottle_size_id);
    const count = Number(washForm.count);
    // `count > 0`, not `!count`: -5 is truthy.
    if (!sizeId || !(count > 0)) {
      notify("Pick a size and how many you washed", "warning");
      return;
    }
    try {
      await honeyStockApi.washGlass(sizeId, count);
      setWashOpen(false);
      setWashForm({ bottle_size_id: "", count: "" });
      load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't record that wash", "danger");
    }
  }

  if (glass === null) return null;

  async function toggleEnforcement(next) {
    /* OPTIMISTIC, then corrected. A toggle that waits for a round trip
       feels broken on a slow connection; one that lies about the result
       is worse, so a refusal puts it back and says why. */
    setEnforcing(next);
    try {
      await honeyStockApi.setJarEnforcement(next);
      notify(next
        ? "Bottling will now stop if you do not have the jars for it."
        : "Bottling will be recorded whatever the jar count says.", "success");
    } catch (err) {
      setEnforcing(!next);
      notify(err.apiMessage || "Could not change that setting", "danger");
    }
  }

  return (
    <Card
      title="Glass"
      className="section-spacing"
      actions={
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {glass.length > 0 && (
            <Button size="sm" variant="secondary" onClick={() => setWashOpen(true)}>Washed jars</Button>
          )}
          <Button size="sm" onClick={() => setTrackOpen(true)}>
            {glass.length > 0 ? "Update counts" : "Start counting"}
          </Button>
        </div>
      }
    >
      {glass.length === 0 ? (
        // Not an error, and not "zero jars" -- this account simply has not
        // opted in. Says what it is FOR, so the choice is informed.
        <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
          You're not counting glass. If you want to, this keeps track of how many
          jars are clean and ready to fill, and how many are dirty and waiting to
          be washed — so you know before a bottling run rather than after.
        </p>
      ) : (
        <DataTable
          rows={glass}
          rowKey="glass_id"
          columns={[
            { key: "bottle_size_label", header: "Size" },
            {
              key: "clean_count",
              header: "Clean",
              // "Ready to fill" is what the number means to somebody
              // planning a run -- the count on its own is just a count.
              render: (r) => `${r.clean_count} ready`,
            },
            {
              key: "dirty_count",
              header: "Dirty",
              render: (r) => (r.dirty_count > 0 ? `${r.dirty_count} to wash` : "—"),
            },
          ]}
          emptyTitle="No sizes counted"
          emptyDescription="Start counting a jar size to see it here."
        />
      )}

      {/* ── STOP ME BOTTLING MORE THAN I HAVE (#360) ──
          Only offered once there is something to enforce against. A
          toggle on an account counting nothing would promise a check that
          could never fire, because an untracked size is never refused. */}
      {glass.length > 0 && (
        <div style={{ marginTop: "var(--space-4)", paddingTop: "var(--space-3)",
                      borderTop: "1px solid var(--color-border)" }}>
          <Toggle
            id="enforce-jar-count"
            checked={enforcing}
            onChange={toggleEnforcement}
            label="Stop a bottling run bigger than the clean count"
          />
          <p style={{ margin: "var(--space-2) 0 0", color: "var(--color-text-muted)",
                      fontSize: "var(--font-size-sm)" }}>
            {/* Says what happens either way, because OFF is a real
                choice and not merely the absence of ON. */}
            {enforcing
              ? "Bottling will be refused if you do not have the jars counted for it, and will say how many you have."
              : "Bottling is recorded whatever the count says, and the clean count stops at zero. Turn this on if you plan runs ahead and want to be told first."}
          </p>
        </div>
      )}

      <Modal
        open={trackOpen}
        onClose={() => setTrackOpen(false)}
        title={glass.length > 0 ? "Update your glass counts" : "Count your glass"}
        footer={<Button onClick={submitTrack}>{trackingNow ? "Replace counts" : "Save counts"}</Button>}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/*
            The counts REPLACE what is recorded (#260).
            Reported from the field: nothing on this screen said so, and
            "Update counts" reads like a correction being applied to a
            running total. It is not -- the MERGE behind it overwrites.
            Somebody who had 30 clean, washed 12 more and typed 12 would
            end up with 12.
          */}
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            {glass.length > 0
              ? "What you enter REPLACES what's recorded — it isn't added to it. Type the total on the shelf right now, not the change."
              : "Whatever's on the shelf right now. These are yours to correct any time — bottling and returns adjust them as you go."}
          </p>
          <Field label="Jar size">
            {(id) => (
              <select
                id={id}
                value={trackForm.bottle_size_id}
                onChange={(e) => setTrackForm((f) => ({ ...f, bottle_size_id: e.target.value }))}
                className="touch-target"
              >
                <option value="">Choose a size…</option>
                {sizes.map((s) => {
                  // Which sizes are already counted, before the size is even
                  // chosen -- otherwise "replaces" is a warning about
                  // something the user cannot yet see.
                  const on = (glass || []).find((g) => g.bottle_size_id === s.bottle_size_id);
                  return (
                    <option key={s.bottle_size_id} value={s.bottle_size_id}>
                      {on ? `${s.label} — now ${on.clean_count} clean, ${on.dirty_count} dirty` : s.label}
                    </option>
                  );
                })}
              </select>
            )}
          </Field>
          {trackingNow && (
            <p style={{ margin: 0 }}>
              Recorded now: <strong>{trackingNow.clean_count} clean</strong>,{" "}
              <strong>{trackingNow.dirty_count} dirty</strong>.
            </p>
          )}
          {/*
            The boxes stay EMPTY on purpose, prefilling deliberately rejected.
            A prefilled count is indistinguishable from one the user just
            typed, so it invites saving numbers nobody actually checked --
            and the whole hazard here is a stale count being written back as
            though it were counted. Empty forces the shelf to be looked at,
            and the hint says what happens if it is not.
          */}
          <Field
            label="Clean, ready to fill"
            hint={trackingNow ? `Leave blank to keep ${trackingNow.clean_count}` : undefined}
          >
            {(id, a11y) => (
              <input id={id} {...a11y} type="number" min="0" className="touch-target"
                value={trackForm.clean_count}
                onChange={(e) => setTrackForm((f) => ({ ...f, clean_count: e.target.value }))} />
            )}
          </Field>
          <Field
            label="Dirty, waiting to be washed"
            hint={trackingNow ? `Leave blank to keep ${trackingNow.dirty_count}` : undefined}
          >
            {(id, a11y) => (
              <input id={id} {...a11y} type="number" min="0" className="touch-target"
                value={trackForm.dirty_count}
                onChange={(e) => setTrackForm((f) => ({ ...f, dirty_count: e.target.value }))} />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={washOpen}
        onClose={() => setWashOpen(false)}
        title="Jars washed"
        footer={<Button onClick={submitWash}>Record</Button>}
      >
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            Moves them from dirty to clean, so they count towards your next
            bottling run.
          </p>
          <Field label="Jar size">
            {(id) => (
              <select
                id={id}
                value={washForm.bottle_size_id}
                onChange={(e) => setWashForm((f) => ({ ...f, bottle_size_id: e.target.value }))}
                className="touch-target"
              >
                <option value="">Choose a size…</option>
                {glass.map((g) => (
                  <option key={g.bottle_size_id} value={g.bottle_size_id}>
                    {g.bottle_size_label} ({g.dirty_count} dirty)
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="How many">
            {(id) => (
              <input id={id} type="number" min="1" className="touch-target"
                value={washForm.count}
                onChange={(e) => setWashForm((f) => ({ ...f, count: e.target.value }))} />
            )}
          </Field>
        </div>
      </Modal>
    </Card>
  );
}
